<template>
  <div class="has-header has-footer">
    <div class="container-style">
      <studyFeatureHeader></studyFeatureHeader>
      <div class="rtsm-container">
        <div class="pt-30 pl-32 pr-32">
          <p class="m-0 fs-20 fw-500 header_title">
            Randomization and Trial Supply Management (RTSM) Integration
          </p>
          <div v-if="selectedVendorDetails" class="rtsm-vendor-details">
            <div class="d-flex">
              <img :src="`static/img/rtsm-vendors/${selectedVendorDetails.icon}`" alt="vendor logo" class="selected-vendor-logo" />
              <div class="ml-16">
                <p data-testId="rtsm-vendor-name" class="fs-16 fw-500 mb-4">{{ selectedVendorDetails.name }}</p>
                <p data-testId="rtsm-vendor-description" class="fs-14">{{ selectedVendorDetails.description }}</p>
              </div>
            </div>
            <p class="fw-500 mt-24 fs-16">Randomization Preference</p>
            <b-card class="rtsm-econsent">
              <p class="fw-bold">With THREAD eConsent</p>
              <p class="fs-12 mt-11">This integration automates data exchange for participant randomization, triggered by completion of eConsent in the THREAD platform.</p>
              <p class="fs-12">This will <span class="fw-bold">automatically lock the following settings:</span></p>
              <div class="rtsm-settings-wrapper">
                <table class="rtsm-settings" aria-describedby="RTSM eConsent locked settings">
                  <tr>
                    <th class="fw-bold fs-14">eConsent settings</th>
                    <th></th>
                  </tr>
                  <tr>
                    <td class="fs-14">After registration</td>
                    <td class="fs-14 text-right"><span class="fa fa-solid fa-lock mr-5"></span>On</td>
                  </tr>
                  <tr>
                    <td class="fs-14">No consent is required</td>
                    <td class="fs-14 text-right"><span class="fa fa-solid fa-lock mr-5"></span>Off</td>
                  </tr>
                </table>
              </div>
            </b-card>
            <p class="mt-24 fs-12 text-right cursor_pointer">
              <a v-b-modal.rtsmVendorModal @click="toggleEditModal()" data-testid="settings-link">{{ getSettingsLinkTitle() }}</a>
            </p>
          </div>
          <b-button data-testId="add-rtsm-vendor-button" v-else class="btn btn-info mt-16 mb-24" v-b-modal.rtsmVendorModal>Add an RTSM Vendor</b-button>
          <p v-if="isStudyActive()" class="fs-12">The <a class="cursor_pointer go-to-design-link" @click="goToDesign()" data-testid="redirect-to-study-design-anchor">study configuration was updated</a> to facilitate the RTSM integration.</p>
          <p v-else class="fs-12">If you want to <a class="cursor_pointer go-to-design-link" @click="goToDesign()" data-testid="redirect-to-study-design-anchor">remove the RTSM Integration</a> please update your selection in the Design section.</p>
        </div>

        <b-modal id="rtsmVendorModal" ref="rtsmVendorModal" :title="getModalTitle()" class="rtsm-vendor-modal" :class="hasAddVendorError || hasVendorFetchError ? 'rtsm-error-modal' : ''" :hide-footer="true" @hidden="hideModal">
          <template v-if="hasAddVendorError || hasVendorFetchError">
            <p data-testid="vendor-error-text">An error occurred while {{ hasVendorFetchError ? "fetching available vendors" : "adding the vendor" }}. Please contact Integrations@threadresearch.com</p>
            <div id="modal-footer" class="d-flex justify-content-end mb-20 mt-24">
              <button v-b-modal.modal-close_visit class="modal-btn btn btn-info btn-primary btn-sm m-1 pl-20 pr-20" @click="hideModal" data-testid="ok-button">
                OK
              </button>
            </div>
          </template>
          <template v-else-if="hasSelectedVendor">
            <p class="fs-12 mb-6">Sponsor Provided Study Code <img class="ml-4 info-icon" src="static/img/grey-info-icon.svg" alt="information icon" role="presentation" id="study-code-info" /></p>
            <b-form-input v-model="studyCode" :state="studyCodeInputState" placeholder="Enter Code" size="sm" :disabled="isStudyActive()" data-testid="sponsor-provider-code"></b-form-input>
            <b-form-invalid-feedback>
              Please enter Study Code
            </b-form-invalid-feedback>
            <b-tooltip target="study-code-info" placement="right" class="text-left" triggers="hover"> This study code is provided by the sponsor or RTSM vendor.</b-tooltip>
            <div id="modal-footer" class="vendor-settings-footer">
              <div class="d-flex mt-24" :class="showEditModal ? 'justify-content-end' : 'justify-content-between'">
                <b-button v-if="!showEditModal" variant="outline-primary" class="btn-sm rtsm-outline-btn" @click="handleModalBack"> <i class="fa fa-chevron-left"></i> Back</b-button>
                <div>
                  <b-button v-if="isStudyActive()" v-b-modal.modal-close_visit class="modal-btn btn btn-info btn-primary btn-sm m-1 pl-20 pr-20" @click="hideModal">
                    OK
                  </b-button>
                  <template v-else>
                    <b-button v-b-modal.modal-close_visit variant="outline-secondary" class="btn btn-sm m-1 pl-20 pr-20" @click="hideModal" data-testid="edit-code-cancel-button">
                      Cancel
                    </b-button>
                    <button v-b-modal.modal-close_visit class="modal-btn btn btn-info btn-primary btn-sm m-1 pl-20 pr-20" @click="showEditModal ? updateRTSMVendor() : addRTSMVendor()" data-testid="submit-provided-code">
                      Save
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <p v-show="showVendorError && !selectedVendor" class="fs-12 rtsm-vendor-error">You must select an RTSM vendor to continue.</p>
            <b-card-group deck>
              <b-card v-for="(vendor, index) in rtsmVendors" :key="index" class="rtsm-vendor" :class="{ 'rtsm-vendor-selected': selectedVendor === vendor.name, 'single-vendor': rtsmVendors.length === 1 }" @click="toggleVendor(vendor.name)">
                <div class="d-flex justify-content-between">
                  <img :src="`static/img/rtsm-vendors/${vendor.icon}`" alt="vendor icon" class="rtsm-vendor-icon" />
                  <img v-if="selectedVendor === vendor.name" src="static/img/custom-green-checked.png" alt="green check" class="selected-vendor-icon" />
                </div>
                <p class="fs-16 fw-500 mt-16 mb-0">{{ vendor.name }}</p>
                <p class="fs-12 mb-0">{{ vendor.description }}</p>
              </b-card>
            </b-card-group>
            <div id="modal-footer" class="d-flex justify-content-end mt-24">
              <b-button v-b-modal.modal-close_visit variant="outline-secondary" class="btn btn-sm m-1 pl-20 pr-20" @click="hideModal" data-testid="select-vendor-cancel-button">
                Cancel
              </b-button>
              <button v-b-modal.modal-close_visit class="modal-btn btn btn-info btn-primary btn-sm m-1 pl-20 pr-20" @click="handleOk">
                Next
              </button>
            </div>
          </template>
        </b-modal>
        <div class="pt-10 pb-16 pl-32">
          <span class="back_link" v-on:click="goBack()"> <i class="fa fa-chevron-left" aria-hidden="true"></i> Back </span>
        </div>
        <div class="footerStyle">
          <div class="container">
            <div class="row" v-if="isStudySummary">
              <b-btn class="btn nextBtn float-left br-25" @click="cancelModify">Cancel modifying</b-btn>
              <b-btn class="btn br-25 disable-btn p-0 pt-5 mr-10" :to="{ name: 'study-summary', params: { studyId: studyId } }">Back to study summary</b-btn>
              <button class="btn br-25 continue-btn mr-10" v-on:click="nextClick()">
                Save and Continue
              </button>
            </div>
            <div class="row" v-if="!isStudySummary">
              <button class="btn br-25 disable-btn mr-10" v-on:click="skipForNow()">
                Skip for now
              </button>
              <button class="btn nextBtn br-25" :disabled="!selectedVendorDetails" v-on:click="nextClick()" data-testId="continue-button-not-summary">
                Save and Continue
              </button>
            </div>
          </div>
        </div>
        <CancelModal ref="cancelConfirmation" />
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import studyFeatureHeader from "../common/study-features-header";
import CancelModal from "./cancel-modify-modal";
import { ACTIVE } from "@/constants/config";
export default {
  name: "rtsm-integration",
  data() {
    return {
      hasAddVendorError: false,
      hasSelectedVendor: false,
      hasVendorFetchError: false,
      originalStudyCode: "",
      rtsmVendors: [],
      selectedVendor: null,
      selectedVendorDetails: null,
      studyCodeInputState: null,
      studyData: {},
      showEditModal: false,
      showVendorError: false,
      studyCode: "",
      studyId: null,
      isStudySummary: window.localStorage.getItem("thread-studySummary") ? JSON.parse(window.localStorage.getItem("thread-studySummary")) : null,
      autoSaveElementTimeout: process.env.VUE_APP_AUTO_SAVE_ELEMENT_LIB_TIMEOUT
    };
  },
  computed: {},
  watch: {
    studyCode: {
      handler(val) {
        if (val) {
          this.studyCodeInputState = null;
        }
      }
    }
  },
  mounted() {
    this.studyId = this.$route.params.studyId;
    window.scrollTo(0, 0);
    this.startAutoSave();
    this.getStudyDetails();
    this.getVendors();
    this.getVendorSelection();
  },
  methods: {
    addRTSMVendor() {
      if (!this.studyCode) {
        this.studyCodeInputState = false;
      } else {
        const selectedVendor = this.rtsmVendors.find(vendor => vendor.name === this.selectedVendor);
        this.$api
          .saveIntegrationVendorSelection({
            studyId: this.studyId,
            studyCode: this.studyCode,
            vendor: selectedVendor
          })
          .then(response => {
            if (response.body) {
              this.$refs.rtsmVendorModal.hide();
              this.selectedVendorDetails = selectedVendor;
              this.originalStudyCode = this.studyCode;
              this.$toastr.success("RTSM Vendor Added.");

              if (this.showEditModal) {
                this.toggleEditModal();
              }
            }
          })
          .catch(() => {
            this.hasAddVendorError = true;
            this.$toastr.error("RTSM Vendor Failed.");
          });
      }
    },
    goBack() {
      window.history.back();
    },
    getModalTitle() {
      const editModalTitle = this.isStudyActive() ? `View ${this.selectedVendor} Vendor Settings` : `Edit Settings ${this.selectedVendor}`;
      const newVendorTitle = this.hasSelectedVendor ? `Settings For ${this.selectedVendor}` : "Select an RTSM Vendor";
      const rtsmTitle = this.showEditModal ? editModalTitle : newVendorTitle;

      return this.hasAddVendorError ? "Add Vendor Error" : rtsmTitle;
    },
    getSettingsLinkTitle() {
      return this.isStudyActive() ? `View settings for ${this.selectedVendor}` : `Edit Settings for ${this.selectedVendor}`;
    },
    getStudyDetails() {
      this.$api.getStudyDataFact(this.studyId).then(response => {
        this.studyData = response.body;
      });
    },
    getVendors() {
      this.$api
        .getIntegrationVendors()
        .then(response => {
          if (response.body) {
            this.rtsmVendors = response.body;
          }
        })
        .catch(() => {
          this.hasVendorFetchError = true;
        });
    },
    getVendorSelection() {
      this.$api
        .getIntegrationVendorSelection(this.studyId)
        .then(response => {
          if (response.body) {
            this.selectedVendorDetails = response.body.vendor;
            this.studyId = response.body.studyId;
            this.studyCode = response.body.studyCode;
            this.originalStudyCode = response.body.studyCode;
            this.selectedVendor = response.body.vendor.name;
            this.hasSelectedVendor = true;
          }
        })
        .catch(() => {});
    },
    handleModalBack() {
      this.hasSelectedVendor = false;
    },
    handleOk() {
      if (!this.selectedVendor) {
        this.showVendorError = true;
      } else {
        this.hasSelectedVendor = true;
      }
    },
    hideModal() {
      this.$refs.rtsmVendorModal.hide();
      this.studyCodeInputState = null;

      if (!this.selectedVendorDetails) {
        this.selectedVendor = "";
        this.hasSelectedVendor = false;
        this.studyCode = "";
      }

      if (this.showEditModal) {
        this.studyCode = this.originalStudyCode;
        this.toggleEditModal();
      }

      if (this.hasAddVendorError) {
        this.hasAddVendorError = false;
      }

      if (this.showVendorError) {
        this.showVendorError = false;
      }
    },
    isStudyActive() {
      return this.studyData.status === ACTIVE;
    },
    nextClick(autoSave = false) {
      if (this.selectedVendorDetails) {
        if (!autoSave) {
          const selectedFeatures = this.$store.getters.getSelectedFeatures;
          const featureIndex = _.findIndex(selectedFeatures, ["route", this.$route.name]);
          if (featureIndex === selectedFeatures.length - 1) {
            this.$router.push({ name: "features-library-final" });
          } else {
            this.$router.push({
              name: selectedFeatures[featureIndex + 1].route,
              params: { studyId: this.studyId }
            });
          }
        }
      } else if (!autoSave) {
        this.toastr.error("Please select RTSM Vendor");
      }
    },
    goToDesign() {
      window.localStorage.setItem("designIndex", "11");
      this.$router.push({
        name: "study-design",
        params: { studyId: this.studyId }
      });
    },
    updateRTSMVendor() {
      if (!this.studyCode) {
        this.studyCodeInputState = false;
      } else {
        this.$api
          .updateIntegrationVendorSelection(this.studyId, {
            studyCode: this.studyCode,
            vendor: this.selectedVendorDetails
          })
          .then(response => {
            if (response.body) {
              this.$refs.rtsmVendorModal.hide();
              this.$toastr.success("RTSM Vendor Settings Updated.");
              this.originalStudyCode = this.studyCode;

              if (this.showEditModal) {
                this.toggleEditModal();
              }
            }
          })
          .catch(() => {
            this.$toastr.error("An error occured while trying to update vendor settings.");
          });
      }
    },
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    skipForNow() {
      const selectedFeatures = this.$store.getters.getSelectedFeatures;
      const featureIndex = _.findIndex(selectedFeatures, ["route", this.$route.name]);
      if (featureIndex === selectedFeatures.length - 1) {
        this.$router.push({ name: "features-library-final" });
      } else {
        this.$router.push({
          name: selectedFeatures[featureIndex + 1].route,
          params: { studyId: this.studyId }
        });
      }
    },
    cancelModify() {
      this.$refs.cancelConfirmation.show();
    },
    startAutoSave() {
      this.autoSaveTimer = setInterval(() => {
        this.nextClick(true);
      }, this.autoSaveElementTimeout);
    },
    stopAutoSave() {
      clearInterval(this.autoSaveTimer);
    },
    toggleVendor(vendorName) {
      if (this.selectedVendor === vendorName) {
        this.selectedVendor = "";
      } else {
        this.selectedVendor = vendorName;
      }
    }
  },
  components: { studyFeatureHeader, CancelModal },
  beforeRouteLeave(to, from, next) {
    this.nextClick(true);
    setTimeout(() => {
      next();
    }, 100);
  },
  beforeDestroy() {
    this.stopAutoSave();
  }
};
</script>

<style scoped lang="scss">
a {
  color: #4c8ce4 !important;
}

.btn {
  font-size: 14px;
}

.btn-outline-secondary {
  border: none !important;
}

.container-style {
  border-radius: 3px;
  background-color: #ffffff;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.1);
}

.has-footer {
  padding-bottom: 100px;
}

.info-icon {
  width: 12px;
  height: 12px;
}

.go-to-design-link {
  text-decoration: underline !important;
}

.row {
  display: block;

  &:after {
    content: "";
    clear: both;
    display: block;
  }
}

.rtsm-container {
  width: 100%;
}

.rtsm-econsent {
  background-color: #f5f8fa;
  box-shadow: 4px 4px 8px 0 rgba(0, 0, 0, 0.1);
}

.rtsm-error-modal {
  /deep/ .modal-dialog {
    max-width: 462px !important;
  }
}

.rtsm-outline-btn {
  border: 1px solid #4c8ce4 !important;
  color: #4c8ce4;
}

.rtsm-settings {
  width: 100%;
}

.rtsm-settings tr {
  border-top: 1px solid rgba(0, 0, 0, 0.125);
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
  border-collapse: collapse;
}

.rtsm-settings td {
  background-color: #ffffff;
}

.rtsm-settings th {
  background-color: #f4f6f8;
}

.rtsm-settings th,
.rtsm-settings td {
  padding: 8px 24px;
}

.rtsm-settings-wrapper {
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.rtsm-vendor-details {
  border: solid 1px #d3dfe4;
  margin-top: 16px;
  margin-bottom: 16px;
  padding: 16px;
}

.rtsm-vendor-error {
  color: #f2533e;
  margin-top: -16px;
}
.rtsm-vendor-modal {
  /deep/ .modal-dialog {
    max-width: 925px;
  }

  /deep/ .modal-header {
    border-bottom: none;
    padding: 24px;
  }

  /deep/ .modal-body {
    padding: 0 24px 16px;
  }

  /deep/ .tooltip-inner {
    max-width: 250px;
    text-align: left !important;
  }

  /deep/ .tooltip .arrow {
    display: none;
  }
}

.rtsm-vendor {
  background-color: #f5f8fa;
  box-shadow: 4px 4px 8px 0 rgba(0, 0, 0, 0.1);
  width: 272.3px !important;
  cursor: pointer;
  flex: none;
}

.rtsm-vendor:nth-child(n + 4) {
  margin-top: 16px;
}

.rtsm-vendor:hover {
  border: solid 1px #2ebd59;
}

.rtsm-vendor-selected {
  border: solid 1px #2ebd59;
  background-color: rgba(46, 189, 89, 0.1);
}

.rtsm-vendor-selected:hover {
  border: solid 2px #2ebd59;
}

.rtsm-vendor-icon {
  max-height: 56px;
}

.rtsm-vendor-radio {
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 50%;
  vertical-align: middle;
  border: 1px solid #d3dfe4;
  appearance: none;
  -webkit-appearance: none;
  outline: none;
  cursor: pointer;
}

.rtsm-vendor-radio:checked {
  background-image: url(/static/img/custom-green-checked.png);
  background-size: contain;
}

.selected-vendor-icon {
  width: 20px;
  height: 20px;
}

.selected-vendor-logo {
  max-height: 77px;
}

.single-vendor {
  width: 414px !important;
  height: 197px !important;
}

.vendor-settings-footer {
  min-width: 430px;
}
</style>
